import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Upload, Download, Trash2, Edit, X, FileText, 
  File, Image, Shield, Award, AlertCircle, Search 
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFramework, setFilterFramework] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    category: '',
    framework: '',
    related_controls: '',
    related_policies: '',
    expiry_date: '',
    tags: ''
  });

  useEffect(() => {
    fetchDocuments();
  }, [refreshKey]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Document uploaded successfully');
      refreshData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const response = await axios.get(`${API}/documents/${docId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setEditFormData({
      title: doc.title,
      description: doc.description || '',
      category: doc.category,
      framework: doc.framework || '',
      related_controls: doc.related_controls ? doc.related_controls.join(', ') : '',
      related_policies: doc.related_policies ? doc.related_policies.join(', ') : '',
      expiry_date: doc.expiry_date || '',
      tags: doc.tags ? doc.tags.join(', ') : ''
    });
    setShowEditModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...editFormData,
        related_controls: editFormData.related_controls ? editFormData.related_controls.split(',').map(c => c.trim()) : [],
        related_policies: editFormData.related_policies ? editFormData.related_policies.split(',').map(p => p.trim()) : [],
        tags: editFormData.tags ? editFormData.tags.split(',').map(t => t.trim()) : []
      };

      await axios.patch(`${API}/documents/${editingDoc.id}`, payload);
      toast.success('Document updated successfully');
      setShowEditModal(false);
      refreshData();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleDelete = async (docId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success('Document deleted successfully');
      refreshData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const toggleDocSelected = (id) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedDocIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected document${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => axios.delete(`${API}/documents/${id}`))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const ok = results.length - failed;
      if (ok) toast.success(`Deleted ${ok} document${ok === 1 ? '' : 's'}`);
      if (failed) toast.error(`Failed to delete ${failed} document${failed === 1 ? '' : 's'}`);
      setSelectedDocIds(new Set());
      refreshData();
    } finally {
      setBulkDeleting(false);
    }
  };

  const getFileIcon = (docType) => {
    const iconClass = "w-8 h-8";
    switch (docType) {
      case 'PDF':
        return <FileText className={`${iconClass} text-red-500`} />;
      case 'PNG':
      case 'JPG':
      case 'JPEG':
        return <Image className={`${iconClass} text-slate-500`} />;
      case 'DOCX':
      case 'DOC':
        return <File className={`${iconClass} text-slate-700`} />;
      default:
        return <File className={`${iconClass} text-slate-700`} />;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Certificate':
        return <Award className="w-4 h-4 text-slate-600" />;
      case 'Report':
        return <FileText className="w-4 h-4 text-slate-600" />;
      case 'Evidence':
        return <Shield className="w-4 h-4 text-green-600" />;
      default:
        return <FileText className="w-4 h-4 text-slate-600" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const filteredDocuments = documents.filter(doc => {
    const categoryMatch = filterCategory === 'all' || doc.category === filterCategory;
    const frameworkMatch = filterFramework === 'all' || doc.framework === filterFramework;
    // Search matches title, description, filename or tags (case-insensitive).
    const q = searchQuery.trim().toLowerCase();
    const tagStr = Array.isArray(doc.tags) ? doc.tags.join(' ') : (doc.tags || '');
    const searchMatch = !q
      || (doc.title || '').toLowerCase().includes(q)
      || (doc.description || '').toLowerCase().includes(q)
      || (doc.file_name || '').toLowerCase().includes(q)
      || tagStr.toLowerCase().includes(q);
    return categoryMatch && frameworkMatch && searchMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
            Document Repository
          </h1>
          <p className="text-slate-600 mt-2">Upload and manage compliance documents, reports, and certificates</p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            className="hidden"
            accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.txt"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents…"
            className="w-full rounded-sm border border-input bg-background pl-9 pr-3 py-2 text-sm"
            data-testid="documents-search-input"
          />
        </div>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="rounded-sm border border-input bg-background px-4 py-2 text-sm"
        >
          <option value="all">All Categories</option>
          <option value="Report">Reports</option>
          <option value="Certificate">Certificates</option>
          <option value="Evidence">Evidence</option>
          <option value="Audit Report">Audit Reports</option>
          <option value="Policy Document">Policy Documents</option>
          <option value="Training Material">Training Materials</option>
          <option value="Uncategorized">Uncategorized</option>
        </select>

        <select
          value={filterFramework}
          onChange={(e) => setFilterFramework(e.target.value)}
          className="rounded-sm border border-input bg-background px-4 py-2 text-sm"
        >
          <option value="all">All Frameworks</option>
          <option value="ISO27001">ISO 27001</option>
          <option value="SOC2">SOC 2</option>
          <option value="Both">Both</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-slate-600">Total Documents</p>
          <p className="text-2xl font-heading font-semibold mt-1">{documents.length}</p>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-slate-600">Certificates</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-slate-600">
            {documents.filter(d => d.category === 'Certificate').length}
          </p>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-slate-600">Reports</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-slate-600">
            {documents.filter(d => d.category === 'Report').length}
          </p>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-slate-600">Evidence</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-green-600">
            {documents.filter(d => d.category === 'Evidence').length}
          </p>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-slate-600">Total Size</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-slate-900">
            {formatFileSize(documents.reduce((sum, d) => {
              const size = typeof d.file_size === 'string' ? 0 : d.file_size;
              return sum + size;
            }, 0))}
          </p>
        </div>
      </div>

      {/* Documents Grid */}
      {selectedDocIds.size > 0 && (
        <div className="flex items-center justify-between bg-slate-900 text-white px-4 py-2 rounded-sm">
          <span className="text-sm">{selectedDocIds.size} selected</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedDocIds(new Set())}
              className="text-xs text-slate-300 hover:text-white"
              data-testid="documents-clear-selection"
            >
              Clear
            </button>
            <Button
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              data-testid="documents-bulk-delete-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              {bulkDeleting ? 'Deleting…' : 'Delete selected'}
            </Button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDocuments.map((doc) => (
          <div key={doc.id} className={`border border-border rounded-sm bg-white p-4 hover:shadow-md transition-shadow ${selectedDocIds.has(doc.id) ? 'ring-2 ring-slate-900' : ''}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedDocIds.has(doc.id)}
                  onChange={() => toggleDocSelected(doc.id)}
                  data-testid={`document-select-${doc.id}`}
                  aria-label={`Select ${doc.title}`}
                />
                {getFileIcon(doc.document_type)}
              </div>
              <div className="flex items-center gap-1">
                {getCategoryIcon(doc.category)}
                <span className="text-xs text-slate-600">{doc.category}</span>
              </div>
            </div>

            <h3 className="font-medium text-slate-900 mb-1 line-clamp-1">{doc.title}</h3>
            <p className="text-xs text-slate-600 mb-2">{doc.doc_id}</p>

            {doc.description && (
              <p className="text-sm text-slate-700 mb-3 line-clamp-2">{doc.description}</p>
            )}

            <div className="flex items-center justify-between text-xs text-slate-700 mb-3">
              <span>{doc.document_type || doc.file_type}</span>
              <span>{typeof doc.file_size === 'string' ? doc.file_size : formatFileSize(doc.file_size)}</span>
            </div>

            {doc.expiry_date && (
              <div className="flex items-center gap-1 mb-3 text-xs">
                <AlertCircle className="w-3 h-3 text-slate-500" />
                <span className="text-slate-600">Expires: {new Date(doc.expiry_date).toLocaleDateString()}</span>
              </div>
            )}

            {doc.tags && (
              <div className="flex flex-wrap gap-1 mb-3">
                {(typeof doc.tags === 'string' ? doc.tags.split(',').map(t => t.trim()) : doc.tags)
                  .slice(0, 3)
                  .map((tag, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-sm">
                      {tag}
                    </span>
                  ))}
              </div>
            )}

            <div className="flex items-center gap-2 pt-3 border-t border-border">
              <button
                onClick={() => handleDownload(doc.id, doc.file_name)}
                className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-800"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={() => handleEdit(doc)}
                className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => handleDelete(doc.id, doc.file_name)}
                className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 ml-auto"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredDocuments.length === 0 && (
        <div className="text-center py-12 border border-border rounded-sm bg-white">
          <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 mb-2">No documents found</p>
          <p className="text-sm text-slate-700">Upload your first document to get started</p>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingDoc && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-heading font-semibold">Edit Document</h2>
              <button onClick={() => setShowEditModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateSubmit} className="p-6 space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    value={editFormData.category}
                    onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                    className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                    required
                  >
                    <option value="Uncategorized">Uncategorized</option>
                    <option value="Report">Report</option>
                    <option value="Certificate">Certificate</option>
                    <option value="Evidence">Evidence</option>
                    <option value="Audit Report">Audit Report</option>
                    <option value="Policy Document">Policy Document</option>
                    <option value="Training Material">Training Material</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="framework">Framework</Label>
                  <select
                    id="framework"
                    value={editFormData.framework}
                    onChange={(e) => setEditFormData({ ...editFormData, framework: e.target.value })}
                    className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                  >
                    <option value="">Not specified</option>
                    <option value="ISO27001">ISO 27001</option>
                    <option value="SOC2">SOC 2</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="related_controls">Related Controls</Label>
                <Input
                  id="related_controls"
                  value={editFormData.related_controls}
                  onChange={(e) => setEditFormData({ ...editFormData, related_controls: e.target.value })}
                  placeholder="A.9.1.2, CC6.1 (comma-separated)"
                />
              </div>

              <div>
                <Label htmlFor="related_policies">Related Policies</Label>
                <Input
                  id="related_policies"
                  value={editFormData.related_policies}
                  onChange={(e) => setEditFormData({ ...editFormData, related_policies: e.target.value })}
                  placeholder="POL-001, POL-002 (comma-separated)"
                />
              </div>

              <div>
                <Label htmlFor="expiry_date">Expiry Date (for certificates)</Label>
                <Input
                  id="expiry_date"
                  type="date"
                  value={editFormData.expiry_date}
                  onChange={(e) => setEditFormData({ ...editFormData, expiry_date: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="tags">Tags</Label>
                <Input
                  id="tags"
                  value={editFormData.tags}
                  onChange={(e) => setEditFormData({ ...editFormData, tags: e.target.value })}
                  placeholder="iso27001, audit, 2024 (comma-separated)"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm">
                  Update Document
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
